<template>
  <Card>
    <div class="py-6 px-4 space-y-12">
      <section class="sm:text-center">
        <div class="flex justify-center items-center">
          <img
            src="/assets/publish/starbannerblueR.png"
            class="aspect-[6/1] hidden xl:block xl:w-60"
          />
          <h1 class="text-3xl sm:text-6xl font-bold">
            {{ $t("site.publish.makemoney.heading-1") }}
          </h1>
          <img
            src="/assets/publish/starbannerblueL.png"
            class="aspect-[6/1] hidden xl:block xl:w-60"
          />
        </div>
        <p class="max-w-prose mx-auto text-lg sm:text-xl mt-8 sm:mt-12">
          {{ $t("site.publish.makemoney.para1-1") }}
        </p>
        <img
          src="/assets/contest/coinstack.png"
          class="mx-auto w-72 mt-4 sm:mt-6"
        />
        <p class="max-w-prose mx-auto text-lg sm:text-xl mt-4 sm:mt-6">
          {{ $t("site.publish.makemoney.para1-2") }}
        </p>
      </section>
      <section class="max-w-6xl mx-auto text-lg sm:text-xl">
        <div class="mx-auto max-w-prose p-4 space-y-4 lg:space-y-6">
          <div class="mx-auto max-w-4xl text-center">
            <p class="text-2xl font-semibold sm:text-4xl">
              {{ $t("site.publish.makemoney.heading-2") }}
            </p>
          </div>
          <p>
            {{ $t("site.publish.makemoney.para2-1") }}
          </p>
          <p>{{ $t("site.publish.makemoney.para2-2") }}</p>
          <ul role="list" class="ml-4 space-y-2 list-decimal">
            <li>{{ $t("site.publish.makemoney.para2-3") }}</li>
            <li>{{ $t("site.publish.makemoney.para2-4") }}</li>
            <li>{{ $t("site.publish.makemoney.para2-5") }}</li>
            <li>{{ $t("site.publish.makemoney.para2-6") }}</li>
          </ul>
          <p>
            {{ $t("site.publish.makemoney.para2-7") }}
          </p>
          <p>
            {{ $t("site.publish.makemoney.para2-8") }}
          </p>
          <p>{{ $t("site.publish.makemoney.para2-9") }}</p>
          <ul role="list" class="ml-4 list-disc space-y-2">
            <li>{{ $t("site.publish.makemoney.para2-10") }}</li>
            <li>{{ $t("site.publish.makemoney.para2-11") }}</li>
            <li>{{ $t("site.publish.makemoney.para2-12") }}</li>
            <li>{{ $t("site.publish.makemoney.para2-13") }}</li>
          </ul>
          <p>
            {{ $t("site.publish.makemoney.para2-14") }}
          </p>
        </div>
      </section>
    </div>
    <div class="mx-auto max-w-prose px-4 flex justify-around py-6">
      <NamiButton
        buttonType="primary"
        :href="`/${mappedLocale}/terms/creatorrewards`"
      >
        {{ $t("site.publish.makemoney.buttonCreatorRewardsPolicy") }}
      </NamiButton>
      <NamiButton buttonType="primary" :href="`/${mappedLocale}/terms/content`">
        {{ $t("site.contest.about.buttonContentPolicy") }}
      </NamiButton>
    </div>

    <div class="py-6 px-4 space-y-12">
      <section class="max-w-6xl mx-auto">
        <div class="mx-auto max-w-prose p-4 space-y-4 lg:space-y-6">
          <div class="mx-auto max-w-4xl text-center">
            <p class="text-2xl font-semibold sm:text-4xl">
              {{ $t("site.contest.about.titleFAQ") }}
            </p>
          </div>
          <dl
            class="mt-10 space-y-6 divide-y divide-neutral-900/10 dark:divide-neutral-100/1 sm:text-base text-sm"
          >
            <HeadlessDisclosure
              as="div"
              v-for="faq in faqs"
              :key="faq.question"
              class="pt-6"
              v-slot="{ open }"
            >
              <dt>
                <HeadlessDisclosureButton
                  class="flex w-full items-start justify-between text-left"
                >
                  <span class="font-semibold leading-7">{{
                    faq.question
                  }}</span>
                  <span class="ml-6 flex h-7 items-center">
                    <IconPlus v-if="!open" class="h-6 w-6" aria-hidden="true" />
                    <IconMinus v-else class="h-6 w-6" aria-hidden="true" />
                  </span>
                </HeadlessDisclosureButton>
              </dt>
              <HeadlessDisclosurePanel as="dd" class="mt-2 pr-12">
                <p class="leading-7 text-neutral-700 dark:text-neutral-300">
                  {{ faq.answer }}
                </p>
              </HeadlessDisclosurePanel>
            </HeadlessDisclosure>
          </dl>
        </div>
      </section>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { IconMinus, IconPlus } from "@tabler/icons-vue";

const { locale, t: translate } = useI18n();
const mappedLocale = computed(() => reverseLocaleMap(locale.value));

const faqs = [
  {
    question: "How do I get paid out?",
    answer:
      "Reward payments are credited to your Organization's payout balance. You can withdraw to your bank once your balance meets the minimum payout threshold, currently set at $25 USD.",
  },
  {
    question: "Will the threshold change in the future?",
    answer:
      "The introductory minimum threshold of 100 Followers and 4,000 Page Views is available to Founding Creators only. After NamiComi leaves Open Beta, the minimum threshold will be 1,000 Followers and 40,000 Page Views. This will not affect Founding Creators.",
  },
  {
    question: "What is a Founding Creator?",
    answer:
      "A Founding Creator is a Creator who makes an Organization, sets up their Payment Account, makes at least one Subscription Tier, and publishes at least one Title with at least one Chapter while NamiComi is in Open Beta. Founding Creators will retain access to their 4 tiers even after NamiComi leaves Open Beta.",
  },
  // More questions...
];

const config = useRuntimeConfig();
const title = ref("Make Money");
const description = ref(
  "Earn money by publishing your story to fans around the world.",
);
const canonical = ref(`${config.public.baseUrl}/en/publish/makemoney`);
definePageMeta({ title: "Make Money" });

useHead({
  title: title,
  link: [{ rel: "canonical", href: canonical }],
  meta: [
    {
      property: "og:title",
      content: `${title.value} - ${config.public.siteName}`,
    },
    {
      property: "og:image",
      content: `${config.public.baseUrl}/assets/contest/coinstack.png`,
    },
    { name: "description", content: description },
  ],
});
</script>
